import { CommonModule } from '@angular/common';
import { Component, Injectable } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { BehaviorSubject } from 'rxjs';

export interface Toast {
  message: string;
  type?: 'success' | 'error' | 'info';
  timeout?: number; // in milliseconds
}

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  constructor() {}

  private toastSubject = new BehaviorSubject<Toast | null>(null);
  toast$ = this.toastSubject.asObservable();

  showToast(toast: Toast) {
    // Emit new toast (kills previous one)
    this.toastSubject.next({...toast});

    // Automatically clear after timeout
    const duration = toast.timeout ?? 3000;
    setTimeout(() => this.clearToast(), duration);
  }

  clearToast() {
    this.toastSubject.next(null);
  }
}

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  template: `
    <div *ngIf="toast" (click)="close()"
      class="tw-fixed tw-z-50 tw-top-2 tw-right-5 tw-px-5 tw-py-3 tw-rounded-2xl tw-shadow-lg tw-transition-all tw-duration-300 tw-text-[#020506] tw-text-base tw-flex tw-items-start tw-justify-center tw-gap-2 tw-max-w-96"
      [ngClass]="{
        'tw-bg-[#e0f8ed] tw-border tw-border-[#1c8351]': toast.type === 'success',
        'tw-bg-[#fff2f4] tw-border-[#e30000]': toast.type === 'error',
        'tw-bg-[#cdf1f8] tw-border-[#45d5eb]': toast.type === 'info',
      }">
        <ng-container [ngSwitch]="toast.type">
          <span *ngSwitchCase="'success'" class="tw-pt-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" fill="#1C8351"/></svg>
          </span>
          <span *ngSwitchCase="'error'" class="tw-pt-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12 6.49L19.53 19.5H4.47L12 6.49ZM12 2.5L1 21.5H23L12 2.5ZM13 16.5H11V18.5H13V16.5ZM13 10.5H11V14.5H13V10.5Z" fill="#E30000"/> </svg>
          </span>
          <span *ngSwitchDefault class="tw-pt-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="#009EE3"/> </svg>
          </span>
        </ng-container>
        <span>
          <p class="tw-font-semibold tw-pt-2">{{title|transloco}}</p>
          <p class="tw-text-sm tw-pb-2 tw-pt-2" [innerHTML]="toast.message"></p>
        </span>
      </div>
  `,
})
export class ToastComponent {
  toast: Toast | null = null;

  constructor(private toastService: ToasterService) {
    this.toastService.toast$.subscribe(toast => {
      this.toast = toast
    });
  }

  get title() {
    if (this.toast.type === 'success')
      return 'general.successMessageLabel';

     if (this.toast.type === 'error')
      return 'general.failedMessageLabel';

     return 'general.infoMessageLabel'; 
  }

  close() {
    this.toastService.clearToast()
  }
}
